import { useState, useEffect, useCallback } from 'react';
import { ReviewsData } from '../utils/ssr/types';
import { fetchReviewsForStore, fetchBatchStoreReviews } from '../utils/node/reviewsIo';

/**
 * Hook to fetch review data for a single store on the client-side
 * Used when reviews weren't pre-fetched on the server
 *
 * @param salesforceValue - The Salesforce SKU for the store
 * @param serverData - Pre-fetched server data (if available)
 * @returns Review data, loading state, and error state
 */
export function useReviewsData(
    salesforceValue: string | null | undefined,
    serverData?: ReviewsData | null
): {
    reviewsData: ReviewsData | null;
    isLoading: boolean;
    error: boolean;
} {
    const [reviewsData, setReviewsData] = useState<ReviewsData | null>(serverData || null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        // If we have server data or no SKU, don't fetch
        if (serverData !== undefined || !salesforceValue) {
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(false);

        fetchReviewsForStore(salesforceValue)
            .then((data) => {
                if (!cancelled) {
                    setReviewsData(data);
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [salesforceValue, serverData]);

    return { reviewsData, isLoading, error };
}

/**
 * Hook to fetch review data for multiple stores in batch
 * Used when user searches a new area and we need reviews for multiple locations
 *
 * @param visibleSKUs - Array of currently visible Salesforce SKUs (what's on screen)
 * @param existingReviews - Already fetched reviews (from server or previous client fetches)
 * @param autoFetch - Whether to automatically fetch missing reviews (default: false for lazy loading)
 * @returns Reviews data map, loading state, and fetch function
 */
export function useBatchReviewsData(
    visibleSKUs: string[],
    existingReviews: Record<string, ReviewsData | null> = {},
    autoFetch: boolean = false
): {
    reviewsData: Record<string, ReviewsData | null>;
    isLoading: boolean;
    fetchReviewsForSKUs: (skus: string[]) => Promise<void>;
} {
    const [reviewsData, setReviewsData] = useState<Record<string, ReviewsData | null>>(existingReviews);
    const [isLoading, setIsLoading] = useState(false);

    const fetchReviewsForSKUs = useCallback(
        async (skusToFetch: string[]) => {
            const missingSKUs = skusToFetch.filter((sku) => !(sku in reviewsData));

            if (missingSKUs.length === 0) {
                return; // All reviews already fetched
            }

            setIsLoading(true);
            try {
                const newReviews = await fetchBatchStoreReviews(missingSKUs);
                setReviewsData((prev) => ({ ...prev, ...newReviews }));
            } catch (error) {
                console.error('Failed to fetch missing reviews:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [reviewsData]
    );

    // Auto-fetch ONLY visible SKUs if autoFetch is enabled
    useEffect(() => {
        if (autoFetch && visibleSKUs.length > 0) {
            fetchReviewsForSKUs(visibleSKUs);
        }
    }, [visibleSKUs, autoFetch, fetchReviewsForSKUs]);

    return { reviewsData, isLoading, fetchReviewsForSKUs };
}
